import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import { Gavel, Scale, ShieldAlert, Briefcase, FileCheck2 } from "lucide-react";
import { GRAVITY_META } from "@/lib/legal";
import { fmtMoney, fmtDays } from "@/lib/chart-utils";

function GravityBadge({ level }) {
  const meta = GRAVITY_META[level] || GRAVITY_META[1];
  return (
    <Badge
      variant="outline"
      className={`border ${meta.badgeClass} font-semibold`}
      data-testid={`gravity-badge-${level}`}
    >
      {meta.label}
    </Badge>
  );
}

function InfractionsTable({ infractions = [], currency = "UM" }) {
  if (!infractions.length) {
    return (
      <Card data-testid="infractions-empty">
        <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
          <FileCheck2 className="h-10 w-10 text-emerald-600" />
          <h3 className="text-lg font-semibold">Aucune infraction détectée</h3>
          <p className="max-w-md text-sm text-muted-foreground">
            Sur la base des données budgétaires et du corpus juridique fourni,
            le modèle n'a identifié aucun indice probant d'infraction.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Sort: gravity desc, then jours_de_vie desc
  const rows = [...infractions].sort((a, b) => {
    if ((b.niveau_gravite || 0) !== (a.niveau_gravite || 0)) {
      return (b.niveau_gravite || 0) - (a.niveau_gravite || 0);
    }
    return (b.jours_de_vie || 0) - (a.jours_de_vie || 0);
  });

  return (
    <Card data-testid="infractions-table-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldAlert className="h-5 w-5 text-rose-600" />
          Infractions potentielles identifiées
          <Badge variant="outline" className="ml-2 font-mono">
            {rows.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table
            className="w-full text-sm"
            data-testid="infractions-table"
          >
            <thead className="border-b border-border bg-muted/40">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="p-3 font-semibold">Qualification</th>
                <th className="p-3 font-semibold">Montant</th>
                <th className="p-3 font-semibold">Jours de vie</th>
                <th className="p-3 font-semibold">Gravité</th>
                <th className="p-3 font-semibold">Articles applicables</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((inf, i) => (
                <tr
                  key={`${inf.qualification}-${i}`}
                  className="border-b border-border last:border-0 align-top"
                  data-testid={`infraction-row-${i}`}
                >
                  <td className="p-3">
                    <div className="font-semibold">{inf.qualification}</div>
                    {inf.description && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        {inf.description}
                      </div>
                    )}
                  </td>
                  <td className="p-3 font-mono">
                    {inf.montant_um != null
                      ? fmtMoney(inf.montant_um, currency)
                      : "—"}
                  </td>
                  <td className="p-3 font-mono font-bold">
                    {inf.jours_de_vie != null
                      ? fmtDays(inf.jours_de_vie)
                      : "—"}
                  </td>
                  <td className="p-3">
                    <GravityBadge level={inf.niveau_gravite} />
                  </td>
                  <td className="p-3">
                    {inf.articles_applicables?.length ? (
                      <ul className="space-y-1.5">
                        {inf.articles_applicables.map((a, j) => (
                          <li
                            key={j}
                            className="text-xs"
                            data-testid={`infraction-${i}-article-${j}`}
                          >
                            <span className="font-semibold">
                              {a.article}
                            </span>
                            <span className="text-muted-foreground">
                              {" "}— {a.texte_titre}
                            </span>
                            {a.extrait && (
                              <div className="mt-0.5 italic text-muted-foreground/80">
                                « {a.extrait} »
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

const RESP_TABS = [
  {
    key: "professionnelle",
    label: "Professionnelle",
    icon: Briefcase,
    testId: "resp-tab-professionnelle",
  },
  {
    key: "civile",
    label: "Civile",
    icon: Scale,
    testId: "resp-tab-civile",
  },
  {
    key: "administrative",
    label: "Administrative",
    icon: FileCheck2,
    testId: "resp-tab-administrative",
  },
  {
    key: "penale",
    label: "Pénale",
    icon: Gavel,
    testId: "resp-tab-penale",
  },
];

function ResponsibilitiesList({ items = [], tabKey }) {
  if (!items.length) {
    return (
      <div
        className="rounded-md border border-dashed border-border bg-muted/20 p-5 text-center text-sm text-muted-foreground"
        data-testid={`resp-empty-${tabKey}`}
      >
        Aucune responsabilité identifiée dans cette catégorie.
      </div>
    );
  }
  return (
    <ul className="space-y-3" data-testid={`resp-list-${tabKey}`}>
      {items.map((it, i) => (
        <li
          key={i}
          className="rounded-md border border-border bg-card p-3"
          data-testid={`resp-${tabKey}-item-${i}`}
        >
          <div className="text-sm">{it.description}</div>
          {it.articles?.length ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {it.articles.map((a, j) => (
                <Badge
                  key={j}
                  variant="outline"
                  className="font-mono text-[11px]"
                >
                  {a}
                </Badge>
              ))}
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function ResponsibilitiesTabs({ responsabilites = {} }) {
  return (
    <Card data-testid="responsibilities-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Scale className="h-5 w-5 text-brand-blue dark:text-brand-gold" />
          Responsabilités engagées
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="professionnelle" data-testid="responsibilities-tabs">
          <TabsList className="h-auto flex-wrap w-full justify-start">
            {RESP_TABS.map((t) => {
              const Icon = t.icon;
              const count = (responsabilites[t.key] || []).length;
              return (
                <TabsTrigger
                  key={t.key}
                  value={t.key}
                  data-testid={t.testId}
                  className="gap-1.5"
                >
                  <Icon className="h-3.5 w-3.5" />
                  {t.label}
                  <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-mono">
                    {count}
                  </span>
                </TabsTrigger>
              );
            })}
          </TabsList>
          {RESP_TABS.map((t) => (
            <TabsContent key={t.key} value={t.key} className="pt-4">
              <ResponsibilitiesList
                items={responsabilites[t.key] || []}
                tabKey={t.key}
              />
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}

export default function LegalAnalysisView({ legal, currency = "UM" }) {
  if (!legal) return null;
  const confidencePct = Math.round((legal.analysis_confidence || 0) * 100);
  const bandClass =
    confidencePct >= 80
      ? "text-emerald-700 dark:text-emerald-400"
      : confidencePct >= 50
      ? "text-amber-700 dark:text-amber-400"
      : "text-rose-700 dark:text-rose-400";

  return (
    <div className="space-y-5" data-testid="legal-analysis-view">
      {/* Synthèse + confidence */}
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center justify-between gap-2 text-base">
            <span>Synthèse vitaliste et juridique</span>
            <span
              className={`text-xs font-mono font-bold ${bandClass}`}
              data-testid="legal-confidence"
            >
              Confiance modèle : {confidencePct}%
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {legal.synthese ? (
            <p
              className="whitespace-pre-line text-sm leading-relaxed text-foreground/90"
              data-testid="legal-synthesis"
            >
              {legal.synthese}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Aucune synthèse fournie par le modèle.
            </p>
          )}
          <Separator className="my-4" />
          <div className="grid grid-cols-1 gap-3 text-xs text-muted-foreground sm:grid-cols-3">
            <div>
              <div className="uppercase tracking-wider">
                Salaire journalier de référence
              </div>
              <div className="mt-0.5 font-mono text-sm text-foreground">
                {fmtMoney(legal.salaire_journalier_reference, currency)}
              </div>
            </div>
            <div>
              <div className="uppercase tracking-wider">Textes mobilisés</div>
              <div className="mt-0.5 font-mono text-sm text-foreground">
                {(legal.source_legal_text_ids || []).length}
              </div>
            </div>
            <div>
              <div className="uppercase tracking-wider">Généré le</div>
              <div className="mt-0.5 font-mono text-sm text-foreground">
                {legal.generated_at
                  ? new Date(legal.generated_at).toLocaleString("fr-FR")
                  : "—"}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <InfractionsTable
        infractions={legal.infractions || []}
        currency={currency}
      />

      <ResponsibilitiesTabs responsabilites={legal.responsabilites || {}} />
    </div>
  );
}
